import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LocationProvider } from "@/lib/location";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Verify from "@/pages/Verify";
import CitizenHome from "@/pages/citizen/Home";
import NewReport from "@/pages/citizen/NewReport";
import MyReports from "@/pages/citizen/MyReports";
import ReportDetail from "@/pages/citizen/ReportDetail";
import Tramites from "@/pages/citizen/Tramites";
import Payments from "@/pages/citizen/Payments";
import Rewards from "@/pages/citizen/Rewards";
import Notifications from "@/pages/citizen/Notifications";
import Community from "@/pages/citizen/Community";
import Events from "@/pages/citizen/Events";
import Surveys from "@/pages/citizen/Surveys";
import Dashboard from "@/pages/intranet/Dashboard";
import Incidents from "@/pages/intranet/Incidents";
import Tasks from "@/pages/intranet/Tasks";
import Expedientes from "@/pages/intranet/Expedientes";
import Collections from "@/pages/intranet/Collections";
import Fines from "@/pages/intranet/Fines";
import Field from "@/pages/intranet/Field";
import Audit from "@/pages/intranet/Audit";
import Analytics from "@/pages/intranet/Analytics";
import EventsAdmin from "@/pages/intranet/EventsAdmin";
import SurveysAdmin from "@/pages/intranet/SurveysAdmin";
import Architecture from "@/pages/Architecture";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
    <Loader2 className="h-7 w-7 animate-spin text-primary" />
  </div>
);

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then(({ data }) => { setUser(data); navigate("/app", { replace: true, state: { user: data } }); })
      .catch(() => navigate("/", { replace: true }));
  }, []); // eslint-disable-line
  return <Spinner />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user.role === "funcionario") return <Navigate to="/app/intranet" replace />;
  if (user.role === "agente") return <Navigate to="/app/campo" replace />;
  return <CitizenHome />;
}

function RoleGate({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="role-forbidden">
        <h2 className="text-xl font-semibold">Módulo restringido</h2>
        <p className="text-muted-foreground mt-2 text-sm">Cambia tu rol con el selector DEMO de la barra superior para acceder.</p>
      </div>
    );
  }
  return children;
}

const STAFF = ["funcionario", "agente"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/verificar" element={<Verify />} />
      <Route path="/verificar/:folio" element={<Verify />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<RoleHome />} />
        <Route path="reportar" element={<NewReport />} />
        <Route path="reportes" element={<MyReports />} />
        <Route path="reportes/:folio" element={<ReportDetail />} />
        <Route path="tramites" element={<Tramites />} />
        <Route path="pagos" element={<Payments />} />
        <Route path="recompensas" element={<Rewards />} />
        <Route path="notificaciones" element={<Notifications />} />
        <Route path="comunidad" element={<Community />} />
        <Route path="eventos" element={<Events />} />
        <Route path="encuestas" element={<Surveys />} />
        <Route path="intranet" element={<RoleGate roles={STAFF}><Dashboard /></RoleGate>} />
        <Route path="incidencias" element={<RoleGate roles={STAFF}><Incidents /></RoleGate>} />
        <Route path="tareas" element={<RoleGate roles={STAFF}><Tasks /></RoleGate>} />
        <Route path="expedientes" element={<RoleGate roles={["funcionario"]}><Expedientes /></RoleGate>} />
        <Route path="cobranza" element={<RoleGate roles={STAFF}><Collections /></RoleGate>} />
        <Route path="multas" element={<RoleGate roles={STAFF}><Fines /></RoleGate>} />
        <Route path="campo" element={<RoleGate roles={STAFF}><Field /></RoleGate>} />
        <Route path="auditoria" element={<RoleGate roles={STAFF}><Audit /></RoleGate>} />
        <Route path="analitica" element={<RoleGate roles={STAFF}><Analytics /></RoleGate>} />
        <Route path="eventos-admin" element={<RoleGate roles={["funcionario"]}><EventsAdmin /></RoleGate>} />
        <Route path="encuestas-admin" element={<RoleGate roles={["funcionario"]}><SurveysAdmin /></RoleGate>} />
        <Route path="arquitectura" element={<Architecture />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} closeButton visibleToasts={3} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <LocationProvider>
            <AppRouter />
          </LocationProvider>
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
